import { Crown, UserX, WifiOff } from 'lucide-react';
import type { Participant } from '@shared/types/room';

interface ParticipantListProps {
  participants: Participant[];
  maxParticipants: number;
  selfId: string | null;
  canKick?: boolean;
  onKick?: (participant: Participant) => void;
  currentParticipantId?: string | null;
  compact?: boolean;
}

export function ParticipantList({ participants, maxParticipants, selfId, canKick = false, onKick, currentParticipantId = null, compact = false }: ParticipantListProps) {
  const sorted = [...participants].sort((a, b) => a.joinedAt - b.joinedAt);
  const emptySlots = Math.max(0, maxParticipants - sorted.filter((p) => p.active).length);
  const rowClass = compact ? 'px-3 py-2 text-sm' : 'px-4 py-3';

  return (
    <ul className="flex flex-col gap-2" aria-label="참여자 목록">
      {sorted.map((p) => {
        const isMe = p.id === selfId;
        const isChoosing = p.id === currentParticipantId;
        return (
          <li
            key={p.id}
            className={`flex items-center justify-between gap-3 rounded-2xl border bg-white ${rowClass} animate-pop-in ${
              isChoosing ? 'border-primary-400 ring-2 ring-primary-100' : 'border-cream-300'
            } ${!p.active ? 'opacity-50' : ''}`}
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <span
                className={`h-2.5 w-2.5 shrink-0 rounded-full ${p.active ? (p.connected ? 'bg-emerald-500' : 'bg-amber-400') : 'bg-charcoal-500/40'}`}
                aria-label={p.active ? (p.connected ? '접속 중' : '연결 끊김') : '퇴장'}
              />
              <span className="truncate font-semibold">{p.nickname}</span>
              {p.isHost && <Crown size={16} className="shrink-0 text-accent-500" aria-label="방장" />}
              {isMe && <span className="shrink-0 rounded-full bg-cream-200 px-2 py-0.5 text-xs text-charcoal-500">나</span>}
              {!p.active && <span className="shrink-0 text-xs text-charcoal-500">나감</span>}
              {p.active && !p.connected && <WifiOff size={14} className="shrink-0 text-amber-500" aria-hidden="true" />}
            </div>
            {canKick && !isMe && p.active && onKick && (
              <button type="button" onClick={() => onKick(p)} className="btn-ghost btn-sm !px-2 text-red-500 hover:text-red-600" aria-label={`${p.nickname} 내보내기`}>
                <UserX size={16} aria-hidden="true" />
              </button>
            )}
          </li>
        );
      })}
      {Array.from({ length: emptySlots }).map((_, i) => (
        <li key={`empty-${i}`} className={`flex items-center gap-2.5 rounded-2xl border border-dashed border-cream-300 bg-cream-50 ${rowClass} text-charcoal-500`}>
          <span className="h-2.5 w-2.5 shrink-0 rounded-full border border-charcoal-500/40" aria-hidden="true" />
          <span>참여 대기 중</span>
        </li>
      ))}
    </ul>
  );
}
